import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Target, Eye, CheckCircle } from 'lucide-react';
import { useLang } from '../context/LanguageContext';
import { t } from '../translations';
import { useScrollReveal } from '../hooks/useScrollReveal';

const TECH_OFFICE = "https://images.unsplash.com/photo-1753715613373-90b1ea010731?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200";

export default function AboutPage() {
  const { lang } = useLang();
  const tr = t[lang];
  const aTr = tr.aboutPage;
  const [refStory, visibleStory] = useScrollReveal();
  const [refMission, visibleMission] = useScrollReveal();
  const [refValues, visibleValues] = useScrollReveal();

  return (
    <main data-testid="about-page">
      {/* Hero */}
      <section
        className="pt-32 pb-16 relative"
        style={{ backgroundColor: '#0D1F1F' }}
      >
        <div
          className="absolute inset-0 opacity-20"
          style={{ backgroundImage: 'radial-gradient(circle at 50% 50%, rgba(79,142,247,0.4) 0%, transparent 60%)' }}
        />
        <div className="section-container relative text-center max-w-3xl mx-auto">
          <h1 className="font-sora font-black text-4xl sm:text-5xl text-white mb-3 animate-fadeInUp">
            {aTr.hero.title}
          </h1>
          <div className="w-14 h-0.5 gradient-bg rounded-full mx-auto mb-6" />
          <p className="text-gray-300 text-base md:text-lg font-inter animate-fadeInUp delay-100">
            {aTr.hero.subtitle}
          </p>
        </div>
      </section>

      {/* Story */}
      <section className="py-24" style={{ backgroundColor: '#0A1A1A' }}>
        <div className="section-container">
          <div ref={refStory} className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <div className={`reveal-left ${visibleStory ? 'visible' : ''}`}>
              <h2 className="font-sora font-bold text-3xl text-white mb-2">{aTr.story.title}</h2>
              <div className="w-10 h-0.5 gradient-bg rounded-full mb-6" />
              <div className="space-y-4">
                <p className="text-gray-300 font-inter leading-relaxed">{aTr.story.desc1}</p>
                <p className="text-gray-300 font-inter leading-relaxed">{aTr.story.desc2}</p>
                <p className="text-gray-300 font-inter leading-relaxed">{aTr.story.desc3}</p>
              </div>
            </div>
            <div className={`reveal-right ${visibleStory ? 'visible' : ''}`}>
              <div className="relative">
                <div
                  className="absolute -inset-4 rounded-3xl opacity-20 blur-2xl"
                  style={{ background: 'linear-gradient(135deg, #4F8EF7, #9B59F5)' }}
                />
                <img
                  src={TECH_OFFICE}
                  alt="Exevori team"
                  className="w-full h-80 object-cover rounded-3xl relative z-10"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Mission & Vision */}
      <section className="py-24" style={{ backgroundColor: '#0D1F1F' }}>
        <div className="section-container">
          <div ref={refMission} className={`text-center max-w-xl mx-auto mb-16 reveal ${visibleMission ? 'visible' : ''}`}>
            <h2 className="font-sora font-bold text-3xl text-white mb-3">{aTr.mission.title}</h2>
          <div className="w-14 h-0.5 gradient-bg rounded-full mx-auto" />
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-4xl mx-auto">
            <div
              className={`exevori-card p-8 reveal ${visibleMission ? 'visible' : ''}`}
              style={{ transitionDelay: '100ms' }}
            >
              <div
                className="w-12 h-12 rounded-xl mb-5 flex items-center justify-center"
                style={{ background: 'linear-gradient(135deg, rgba(79,142,247,0.2), rgba(155,89,245,0.2))' }}
              >
                <Target size={22} className="text-exevori-primary" strokeWidth={1.5} />
              </div>
              <h3 className="font-sora font-bold text-white text-lg mb-3">
                {lang === 'fr' ? 'Notre Mission' : 'Our Mission'}
              </h3>
              <p className="text-gray-300 font-inter leading-relaxed">{aTr.mission.mission}</p>
            </div>
            <div
              className={`exevori-card p-8 reveal ${visibleMission ? 'visible' : ''}`}
              style={{ transitionDelay: '200ms' }}
            >
              <div
                className="w-12 h-12 rounded-xl mb-5 flex items-center justify-center"
                style={{ background: 'linear-gradient(135deg, rgba(79,142,247,0.2), rgba(155,89,245,0.2))' }}
              >
                <Eye size={22} className="text-exevori-primary" strokeWidth={1.5} />
              </div>
              <h3 className="font-sora font-bold text-white text-lg mb-3">
                {lang === 'fr' ? 'Notre Vision' : 'Our Vision'}
              </h3>
              <p className="text-gray-300 font-inter leading-relaxed">{aTr.mission.vision}</p>
            </div>
          </div>
        </div>
      </section>

      {/* Values */}
      <section className="py-24" style={{ backgroundColor: '#0A1A1A' }}>
        <div className="section-container">
          <div ref={refValues} className={`text-center mb-16 reveal ${visibleValues ? 'visible' : ''}`}>
            <h2 className="font-sora font-bold text-3xl text-white mb-3">{aTr.values.title}</h2>
          <div className="w-14 h-0.5 gradient-bg rounded-full mx-auto" />
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {aTr.values.items.map((val, i) => (
              <div
                key={i}
                data-testid={`value-card-${i}`}
                className={`exevori-card p-8 reveal ${visibleValues ? 'visible' : ''}`}
                style={{ transitionDelay: `${i * 100}ms` }}
              >
                <div className="flex items-center gap-3 mb-4">
                  <CheckCircle size={18} className="text-exevori-primary flex-shrink-0" />
                  <h3 className="font-sora font-semibold text-white">{val.title}</h3>
                </div>
                <p className="text-gray-400 text-sm font-inter leading-relaxed">{val.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-24" style={{ backgroundColor: '#0D1F1F' }}>
        <div className="section-container text-center max-w-xl mx-auto">
          <h2 className="font-sora font-bold text-3xl text-white mb-4">{aTr.cta.title}</h2>
          <p className="text-gray-300 font-inter mb-8">{aTr.cta.subtitle}</p>
          <Link to="/devis" data-testid="about-cta" className="btn-primary">
            {aTr.cta.btn} <ArrowRight size={16} />
          </Link>
        </div>
      </section>
    </main>
  );
}
